import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Search, Plus, Zap, Settings, Eye } from 'lucide-react';
import { formatDate } from '../../lib/utils';

const PAGE_SIZE = 20;

export function SmartCorrectListPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(value: string) {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }

  const { data, isLoading } = useQuery({
    queryKey: ['smart-correct', page, debouncedSearch],
    queryFn: () =>
      api
        .get('/api/smart-correct/list', {
          params: {
            offset: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            search: debouncedSearch || undefined,
          },
        })
        .then((r) => r.data),
  });

  const instances = data?.query?.data ?? [];
  const total = data?.query?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#cc0404]/10 flex items-center justify-center">
            <Zap className="h-5 w-5 text-[#cc0404]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Smart Correct</h1>
            <p className="text-sm text-gray-500">
              Vendor matching & auto-correction services
            </p>
          </div>
        </div>
        <Link to="/smart-correct/setup">
          <Button>
            <Plus className="h-4 w-4 mr-2" /> New Smart Correct
          </Button>
        </Link>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search Smart Correct services..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10 max-w-sm"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">
          Loading Smart Correct services...
        </div>
      ) : instances.length === 0 ? (
        <Card className="p-12 text-center">
          <Zap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Smart Correct services yet</h3>
          <p className="text-gray-500 mb-6">
            Create your first Smart Correct service to start matching vendors automatically.
          </p>
          <Link to="/smart-correct/setup">
            <Button>
              <Plus className="h-4 w-4 mr-2" /> Create Smart Correct
            </Button>
          </Link>
        </Card>
      ) : (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service Name</TableHead>
                  <TableHead>Vantage Instance</TableHead>
                  <TableHead>Match Field</TableHead>
                  <TableHead>Threshold</TableHead>
                  <TableHead>Vendors</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instances.map((inst: any) => {
                  const config = inst.config?.matching ?? {};
                  const vendorCount = inst.config?.vendorCount ?? inst.confirmed_vendor_count ?? 0;
                  return (
                    <TableRow key={inst.service_instance_id}>
                      <TableCell>
                        <Link
                          to={`/smart-correct/${inst.service_instance_id}`}
                          className="text-[#cc0404] hover:underline font-medium"
                        >
                          {inst.service_title}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {inst.config?.vantage?.app_id ? (
                          <span className="font-mono text-xs">{inst.config.vantage.app_id.substring(0, 8)}...</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="capitalize">
                        {config.matchField || '—'}
                      </TableCell>
                      <TableCell>
                        {config.fuzzyThreshold ? `${config.fuzzyThreshold}%` : '—'}
                      </TableCell>
                      <TableCell>{vendorCount}</TableCell>
                      <TableCell>
                        <Badge variant={inst.is_active ? 'success' : 'secondary'}>
                          {inst.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {formatDate(inst.created)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/smart-correct/${inst.service_instance_id}`}>
                            <Button variant="ghost" size="icon" title="View Details">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Link to={`/smart-correct/${inst.service_instance_id}/config`}>
                            <Button variant="ghost" size="icon" title="Configure">
                              <Settings className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}